import { t } from "adapters/i18n";
import history from "features/app/History";
import ConciliationManager from "features/conciliacao/conciliacaoManager";
import type { Dispatch } from "react-redux";
import type { ThunkDispatch } from "redux-thunk";
import { exibirCarregando, notifyError, notifySuccess } from "redux/actions";
import {
  HAS_CREEP_FEEDING,
  HAS_PASTURES,
  SET_FARM,
  SET_MAP_CENTER_COORDS,
  UPDATE_CONCILIATION_DATE,
  LIST_FARMS,
  UPDATE_FARM_BENCHMARK_PARTICIPATION,
} from "redux/actions/actionsConstants";
import type { BenchmarkDispatchAction } from "redux/actions/benchmarkActions";
import { fetchBenchmarkAccessForLoggedUser } from "redux/operations/benchmarkOperations";
import { RootState } from "redux/reducers";
import BenchmarkService from "services/BenchmarkService";
import type {
  FazendaBenchmarkGrantBody,
  FazendaBenchmarkRevokeBody,
} from "services/BenchmarkService";
import CochoService from "services/CochoService";
import FazendaService from "services/FazendaService";
import PastoService from "services/PastoService";
import ToggleService from "services/ToggleService";
import { DateUtils } from "shared/dateUtils";
import { getErrorMessage } from "shared/errorUtils";
import {
  getFarmStateFromStorage,
  getSelectedFarmIdFromStorage,
  getUserIdFromStorage,
  setFarmStateInStorage,
} from "shared/storageUtils";
import { Fazenda, ToggleFeature } from "shared/types";
import type { Coordinate } from "../reducers/farmReducer";

export type FarmDispatchAction =
  | {
      type: typeof SET_MAP_CENTER_COORDS;
      payload: Coordinate;
    }
  | {
      type: typeof SET_FARM;
      payload: { farmId: number; toggles: ToggleFeature[] };
    }
  | {
      type: typeof LIST_FARMS;
      payload: { farms: Fazenda[]; farmId: number | null };
    }
  | {
      type: typeof UPDATE_CONCILIATION_DATE;
      payload: { conciliationStartDate: string | null };
    }
  | {
      type: typeof HAS_PASTURES;
      payload: boolean;
    }
  | {
      type: typeof HAS_CREEP_FEEDING;
      payload: boolean;
    }
  | {
      type: typeof UPDATE_FARM_BENCHMARK_PARTICIPATION;
      payload: { participacaoBenchmark: boolean };
    };

type BenchmarkThunkDispatch = ThunkDispatch<
  RootState,
  unknown,
  BenchmarkDispatchAction
>;

/**
 * `Dispatch<FarmDispatchAction>` can't type-check dispatching a thunk from
 * another domain, so this narrows the cast to a single spot instead of
 * widening every farmActions dispatch (which breaks the untyped action
 * creators from redux/actions, e.g. notifyError/notifySuccess).
 */
function dispatchFetchBenchmarkAccessForLoggedUser(
  dispatch: Dispatch<FarmDispatchAction>,
) {
  (dispatch as unknown as BenchmarkThunkDispatch)(
    fetchBenchmarkAccessForLoggedUser(),
  );
}

export const setFarm =
  (farmId: number) => async (dispatch: Dispatch<FarmDispatchAction>) => {
    const toggleResponse = await ToggleService.listarToggles(farmId);

    dispatch({
      type: SET_FARM,
      payload: {
        farmId: farmId,
        toggles: toggleResponse.data ?? [],
      },
    } satisfies FarmDispatchAction);

    dispatchFetchBenchmarkAccessForLoggedUser(dispatch);
  };

export const setMapCenterForCity =
  (latitude: number, longitude: number) =>
  async (dispatch: Dispatch<FarmDispatchAction>) => {
    const res = await FazendaService.retornaCidadeComCoordenadas(
      latitude,
      longitude,
    );

    const coords: Coordinate = res?.results?.[0]?.geometry?.location ?? {
      lat: 0,
      lng: 0,
    };

    dispatch({
      type: SET_MAP_CENTER_COORDS,
      payload: coords,
    } satisfies FarmDispatchAction);
  };

export const setMapCenterCoordinates =
  (coordinate: Coordinate) => (dispatch: Dispatch<FarmDispatchAction>) => {
    dispatch({
      type: SET_MAP_CENTER_COORDS,
      payload: coordinate,
    } satisfies FarmDispatchAction);
  };

/**
 * @todo: move service response handling to the service
 **/
export const setFarmsForClient =
  (clientId: number) => async (dispatch: Dispatch<FarmDispatchAction>) => {
    try {
      const userId = getUserIdFromStorage();
      const storedFarmId = getSelectedFarmIdFromStorage();

      const clientFarms = await FazendaService.listarFazendasCliente(
        userId,
        clientId,
      );

      const farmList = clientFarms.data ?? [];

      const lastSelectedFarmId =
        // @ts-expect-error - This should be typed properly in the service
        farmList.find((item) => item.IdFazenda === storedFarmId)?.IdFazenda ??
        farmList[0]?.IdFazenda ??
        null;

      dispatch({
        type: LIST_FARMS,
        payload: {
          farms: farmList,
          farmId: lastSelectedFarmId,
        },
      } satisfies FarmDispatchAction);
    } catch (error) {
      if (
        error != null &&
        typeof error === "object" &&
        "status" in error &&
        error.status === 401
      ) {
        history.push("/");
        dispatch(notifyError({ mensagem: t("login:authentication-error") }));

        return;
      }

      dispatch(notifyError({ mensagem: t("locales:fetch-farms-error") }));
    }
  };

export const setHasPasturesForFarm =
  (farmId: number) => async (dispatch: Dispatch<FarmDispatchAction>) => {
    try {
      const farmPastures = await PastoService.listaPastosPorIdFazenda(farmId);
      const hasPastures = farmPastures.data?.length > 0;
      dispatch({
        type: HAS_PASTURES,
        payload: hasPastures,
      } satisfies FarmDispatchAction);
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("locales:fetch-farm-pastures-error"),
      );

      dispatch(notifyError({ mensagem: message }));
    }
  };

export const setHasCreepFeedingForFarm =
  (farmId: number) => async (dispatch: Dispatch<FarmDispatchAction>) => {
    try {
      const farmCreepFeeding =
        await CochoService.getCochoCreepPorIdFazenda(farmId);
      const hasCreepFeeding = farmCreepFeeding?.data?.length > 0;

      dispatch({
        type: HAS_CREEP_FEEDING,
        payload: hasCreepFeeding,
      } satisfies FarmDispatchAction);
    } catch (error) {
      dispatch({
        type: HAS_CREEP_FEEDING,
        payload: false,
      } satisfies FarmDispatchAction);

      const message = getErrorMessage(
        error,
        t("locales:verify-creep-feeding-error"),
      );
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const updateConciliationStartDate =
  (dateString: string) =>
  async (dispatch: Dispatch<FarmDispatchAction>, getState: () => RootState) => {
    try {
      dispatch(exibirCarregando(true));
      const { fazenda } = getState();
      const farmId = fazenda.FazendaSelecionada;
      const timezone = fazenda.currentFarm?.TimezoneFazenda ?? 0;

      if (farmId == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const parsedDate = DateUtils.parse(dateString)
        .utcOffset(timezone)
        .toDate();

      const result = await ConciliationManager.salvaDataInicioConciliacao(
        parsedDate,
        farmId,
      );

      dispatch({
        type: UPDATE_CONCILIATION_DATE,
        payload: { conciliationStartDate: result.DataInicioConciliacao },
      } satisfies FarmDispatchAction);

      const localFarmState = getFarmStateFromStorage();
      setFarmStateInStorage({
        ...(localFarmState ?? {}),
        DataInicioConciliacao: result.DataInicioConciliacao,
      });

      dispatch(
        notifySuccess({
          mensagem: t("conciliation:update-conciliation-date-success"),
        }),
      );
    } catch (error) {
      dispatch({
        type: UPDATE_CONCILIATION_DATE,
        payload: { conciliationStartDate: null },
      } satisfies FarmDispatchAction);

      const message = getErrorMessage(
        error,
        t("conciliation:update-conciliation-date-error"),
      );
      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };

export const getFarmKml =
  () =>
  async (dispatch: Dispatch<FarmDispatchAction>, getState: () => RootState) => {
    try {
      const { fazenda } = getState();
      const farmId = fazenda.FazendaSelecionada;

      if (farmId == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const kmlResponse = await FazendaService.obtemKmlFazenda(farmId);

      return kmlResponse;
    } catch (error) {
      const err = getErrorMessage(error);
      dispatch(notifyError({ mensagem: err }));
    }
  };

export const updateFarmBenchmarkParticipation =
  (participacaoBenchmark: boolean) =>
  async (dispatch: Dispatch<FarmDispatchAction>, getState: () => RootState) => {
    try {
      dispatch(exibirCarregando(true));
      const { fazenda } = getState();
      const idFazenda = fazenda.FazendaSelecionada;
      const currentFarm = fazenda.currentFarm;
      const nomeDoCliente = localStorage.getItem("nomeDoCliente") ?? undefined;

      if (idFazenda == null || currentFarm == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      if (nomeDoCliente == null) {
        throw new Error(t("common:no-client-selected"));
      }

      const body: FazendaBenchmarkGrantBody | FazendaBenchmarkRevokeBody =
        participacaoBenchmark
          ? {
              acao: "grant",
              customer_name: nomeDoCliente,
              country_alpha2: currentFarm.Pais,
              region: currentFarm.Estado,
              city: currentFarm.Cidade,
            }
          : { acao: "revoke", customer_name: nomeDoCliente };

      await BenchmarkService.putFazendaBenchmarkAccess(currentFarm.Nome, body);

      const participacaoBenchmarkStatus = participacaoBenchmark;

      dispatch({
        type: UPDATE_FARM_BENCHMARK_PARTICIPATION,
        payload: {
          participacaoBenchmark: participacaoBenchmarkStatus,
        },
      } satisfies FarmDispatchAction);

      const localFarmState = getFarmStateFromStorage();
      if (localFarmState) {
        setFarmStateInStorage({
          ...localFarmState,
          Fazendas: localFarmState.Fazendas?.map((fazenda) =>
            fazenda.IdFazenda === idFazenda
              ? {
                  ...fazenda,
                  ParticipacaoBenchmark: participacaoBenchmarkStatus,
                }
              : fazenda,
          ),
        });
      }

      dispatch(
        notifySuccess({
          mensagem: participacaoBenchmarkStatus
            ? t("benchmark:benchmark-enabled-success")
            : t("benchmark:benchmark-disabled-success"),
        }),
      );

      dispatchFetchBenchmarkAccessForLoggedUser(dispatch);

      return participacaoBenchmarkStatus;
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("benchmark:benchmark-toggle-error"),
      );
      dispatch(notifyError({ mensagem: message }));
      throw error;
    } finally {
      dispatch(exibirCarregando(false));
    }
  };

export const getBenchmarkParticipationStatus =
  () =>
  async (dispatch: Dispatch<FarmDispatchAction>, getState: () => RootState) => {
    try {
      const { fazenda } = getState();
      const farmName = fazenda.currentFarm?.Nome;
      const clientName = localStorage.getItem("nomeDoCliente") ?? undefined;

      if (farmName == null) {
        throw new Error(t("common:no-farm-selected"));
      }
      if (clientName == null) {
        throw new Error(t("common:no-client-selected"));
      }
      const response = await BenchmarkService.getFazendaBenchmarkAccess(
        farmName,
        clientName,
      );
      const hasBenchmarkAccess = Boolean(response.has_access);

      dispatch({
        type: UPDATE_FARM_BENCHMARK_PARTICIPATION,
        payload: {
          participacaoBenchmark: hasBenchmarkAccess,
        },
      } satisfies FarmDispatchAction);

      return hasBenchmarkAccess;
    } catch (error) {
      const message = getErrorMessage(
        error,
        t("errors.benchmark:unexpected-error"),
      );
      dispatch(notifyError({ mensagem: message }));
      throw error;
    }
  };
